import React from 'react';
import useAPI from '../hooks/useAPI';
import { getSiteAlerts } from '../services/api';

/**
 * Cache the site-alerts response once per page load and share it across all
 * <SiteAlertBullet> instances. Uses a module-level promise so the API is
 * called once even when the component is mounted in many table rows.
 */
let _alertsPromise = null;
function fetchAlertsCached() {
  if (!_alertsPromise) _alertsPromise = getSiteAlerts();
  return _alertsPromise;
}
// Refresh when the user clicks something / route changes — exposed to manually
// reset if needed. For now we just refresh on full page reload.

/**
 * Tiny blinking red dot rendered next to a project name when the project has
 * an active site-level alert (BLE_ERROR or OFFLINE). Hover for a tooltip
 * listing every active alert and the time it started.
 *
 * Usage:  <SiteAlertBullet projectId={p.id} />
 *
 * Renders nothing if there are no alerts for this project.
 */
export default function SiteAlertBullet({ projectId, size = 9 }) {
  const { data } = useAPI(fetchAlertsCached);
  if (!projectId || !data?.projects) return null;
  const alerts = data.projects[projectId] || [];
  if (!alerts.length) return null;

  const tooltip = alerts.map(a => {
    const since = a.since ? new Date(a.since).toLocaleString() : '';
    return `${a.label}${since ? `  (since ${since})` : ''}`;
  }).join('\n');

  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        marginLeft: 6,
        marginRight: 4,
        borderRadius: '50%',
        background: '#d63031',
        boxShadow: '0 0 6px rgba(214, 48, 49, 0.7)',
        verticalAlign: 'middle',
        animation: 'siteAlertBlink 1s ease-in-out infinite',
        cursor: 'help',
      }}
      aria-label="Site alert"
    />
  );
}

// Inject the @keyframes once per page (CRA doesn't have inline keyframes).
if (typeof document !== 'undefined' &&
    !document.getElementById('site-alert-keyframes')) {
  const style = document.createElement('style');
  style.id = 'site-alert-keyframes';
  style.textContent = `
    @keyframes siteAlertBlink {
      0%, 100% { opacity: 1; transform: scale(1); }
      50%      { opacity: 0.35; transform: scale(0.85); }
    }
  `;
  document.head.appendChild(style);
}
